/**
 * Post-authentication destinations (PRD §16).
 *
 * An open redirect on a login page hands attackers a credible phishing hop, so
 * a destination read from the URL is only ever honoured when it is a relative
 * path within this origin. `relativePathSchema` is the same rule the server
 * enforces, imported rather than restated so the two cannot drift.
 */
import { relativePathSchema } from '@mindpeer/auth-shared';

export const DEFAULT_DESTINATION = '/account';

/** The query parameter modules and internal links use to preserve intent. */
export const NEXT_PARAM = 'next';

/**
 * Validate a candidate destination, falling back to the account home.
 * Protocol-relative (`//evil.example`) and absolute URLs are both rejected by
 * the shared schema's pattern.
 */
export function safeDestination(candidate: string | null | undefined): string {
  if (!candidate) return DEFAULT_DESTINATION;
  const result = relativePathSchema.safeParse(candidate);
  return result.success && result.data ? result.data : DEFAULT_DESTINATION;
}

/** Read the destination out of a location's query string. */
export function destinationFrom(search: string): string {
  return safeDestination(new URLSearchParams(search).get(NEXT_PARAM));
}

/** Build a sign-in link that returns the user to where they were headed. */
export function signInLinkTo(destination: string): string {
  if (destination === DEFAULT_DESTINATION) return '/sign-in';
  return `/sign-in?${NEXT_PARAM}=${encodeURIComponent(destination)}`;
}
